import { useMemo, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useHistorico } from '../../../hooks/useHistorico';
import { useUsuarios } from '../../demands/hooks/useData';
import { useProcessos } from '../../../hooks/useProcessos';
import { useSetores } from '../../demands/hooks/useData';
import { useDemandModals } from './useDemandModals';
import { useDemandEdit } from './useDemandEdit';
import { useDemandActions } from './useDemandActions';
import { useDemandAttachments } from './useDemandAttachments';
import { useDemandObservers } from './useDemandObservers';
import { useDemandHistory } from './useDemandHistory';
import type { Demanda } from '../../../models'

interface DemandaExtended extends Demanda {
  demandados?: any[];
}

interface DemandDetailsProps {
  demanda: DemandaExtended;
  isEditing?: boolean;
  onEditToggle?: () => void;
  onSave?: (updatedDemanda: Partial<DemandaExtended>) => void;
}

export const useDemandDetails = ({
  demanda,
  isEditing = false,
  onEditToggle,
  onSave
}: DemandDetailsProps) => {
  // Hook de autenticação
  const { user: currentUser } = useAuth();

  // Hook para histórico
  const { historico, loading: historicoLoading, error: historicoError, refetch: refetchHistorico } = useHistorico(demanda.id);

  // Hooks para dados
  const { processos, loading: processosLoading } = useProcessos();
  const { setores, loading: setoresLoading } = useSetores();
  const { usuarios, fetchUsuariosdoSetor, refetch: fetchTodosUsuarios } = useUsuarios(false);

  useEffect(() => {
    if (demanda.setor_fk) {
      fetchUsuariosdoSetor(demanda.setor_fk);
    } else {
      fetchTodosUsuarios();
    }
  }, [demanda.setor_fk, fetchUsuariosdoSetor, fetchTodosUsuarios]);
  // Hook de modais
  const modalHooks = useDemandModals();

  // Hook de edição
  const editHooks = useDemandEdit({
    demanda,
    isEditing,
    onSave: onSave || (() => {}),
    onEditToggle: onEditToggle || (() => {}),
    refetchHistorico,
    showConfirm: modalHooks.showConfirm,
    setNavigationModal: modalHooks.setNavigationModal
  });

  // Hook de ações
  const actionHooks = useDemandActions({
    demanda,
    onSave: onSave || (() => {}),
    refetchHistorico,
    showAlert: modalHooks.showAlert,
    showConfirm: modalHooks.showConfirm,
    showPrompt: modalHooks.showPrompt,
    setSolveModal: modalHooks.setSolveModal,
    setAssignModal: modalHooks.setAssignModal,
    setRemandModal: modalHooks.setRemandModal,
    setCommentAttachModal: modalHooks.setCommentAttachModal,
    setRemoveAssignedModal: modalHooks.setRemoveAssignedModal,
    setFinalizeModal: modalHooks.setFinalizeModal,
    setPromptModal: modalHooks.setPromptModal
  });

  // Hook de anexos
  const attachmentHooks = useDemandAttachments({
    demandaId: demanda.id || 0,
    refetchHistorico,
    showAlert: modalHooks.showAlert,
    showConfirm: modalHooks.showConfirm,
    setCommentAttachModal: modalHooks.setCommentAttachModal
  });

  // Hook de observadores
  const observerHooks = useDemandObservers({
    demanda,
    refetchHistorico,
    showAlert: modalHooks.showAlert,
    setAddObserversModal: modalHooks.setAddObserversModal
  });

  // Hook de histórico
  const historyHooks = useDemandHistory({ historico });

  // Converter dados para o formato esperado pelo UnifiedProcessSelector
  const processoOptionsForSelector = useMemo(() => 
    processos.map(processo => ({
      value: processo.id,
      label: processo.nome_processo,
      setor_processo_fk: processo.setor_processo_fk
    })), [processos]
  );

  const setorOptionsForSelector = useMemo(() => 
    setores.map(setor => ({
      value: setor.id,
      label: setor.nome_setor
    })), [setores]
  );

  // Converter usuários para opções de select
  const userOptions = useMemo(() => 
    usuarios.map(user => ({
      value: user.id,
      label: user.name,
      name: user.name,
      email: user.email,
    })), [usuarios]
  );

  // Toolbar actions memoizado
  const toolbarActions = useMemo(() => {
    return {
      // Ações serão definidas baseadas nas permissões e estado da demanda
      canEdit: true, // Será definido baseado nas permissões
      canAssign: true, // Será definido baseado nas permissões
      canSolve: true, // Será definido baseado nas permissões
      canRemand: true, // Será definido baseado nas permissões
      canAddObservers: true, // Será definido baseado nas permissões
      canComment: true, // Será definido baseado nas permissões
    };
  }, []);

  return {
    // Dados básicos
    currentUser,
    historico,
    historicoLoading,
    historicoError,
    refetchHistorico,
    
    // Dados de apoio
    processos,
    processosLoading,
    setores,
    setoresLoading,
    usuarios,
    processoOptionsForSelector,
    setorOptionsForSelector,
    userOptions,
    
    // Toolbar
    toolbarActions,
    
    // Todos os hooks
    ...modalHooks,
    ...editHooks,
    ...actionHooks,
    ...attachmentHooks,
    ...observerHooks,
    ...historyHooks
  };
};